/// <amd-module name="Build/Common/Library/Legacy/Components/ShelvesetPickerDialog" />
import * as React from "react";

import { BuildPersona } from "Build/Common/Library/Components/BuildPersona";
import { getAvatarUrl } from "Build/Common/Library/Identity";
import { ShelvesetPickerActionCreator, ShelvesetPickerActionHub } from "Build/Common/Library/Legacy/Components/ShelvesetAction";
import * as Resources from "Build/Common/Library/Legacy/Resources";
import { ISourceProvider, RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";

import { TfvcShelvesetRef } from "VC/Common/Contracts/Git";

import { format } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssPageService } from "VSS/Platform/Page";

import { ObservableValue } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { Dialog } from "VSSUI/Dialog";
import { IdentityPickerDropdown, IIdentity } from "VSSUI/IdentityPicker";
import { IListItemDetails, IListRow, ListItem, ListSelection, ScrollableList } from "VSSUI/List";
import { ago } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";


export interface IShelvesetPickerDialogState {
   shelvesets: TfvcShelvesetRef[];
   owner: IdentityRef;
   selectedShelvesetName: string;
}

export interface IShelvesetPickerDialogProps {
    pageContext?: IVssPageContext;
    project?: string;
    actionHub: ShelvesetPickerActionHub;
}

export class ShelvesetPickerDialog extends React.Component<IShelvesetPickerDialogProps, IShelvesetPickerDialogState> {
    private _actionCreator: ShelvesetPickerActionCreator;
    private _shelvesets: TfvcShelvesetRef[];
    private _owner: IdentityRef;
    private _pickedIdentity: ObservableValue<IIdentity | undefined> = new ObservableValue<IIdentity | undefined>(undefined);
    private _sourceProvider: ISourceProvider;
    private _selectedShelvesetName: string = "";
    private _listSelection: ListSelection;
    private _unloadableImageUrls: Set<string>;

    constructor(props: IShelvesetPickerDialogProps) {
       super(props);

       this._actionCreator = new ShelvesetPickerActionCreator({actionHub: this.props.actionHub});
       this._listSelection = new ListSelection();
       this._unloadableImageUrls = new Set<string>();
      
       this._shelvesets = [];
       this._setCurrentUser();
       this.state = this._getState();
       this._pickedIdentity.value = this._convertIdentityRefToPickerIdentity(this._owner);

       const sourceProviderService = this.props.pageContext ? this.props.pageContext.getService<ISourceProviderService>("ISourceProviderService") : undefined; 
       this._sourceProvider = sourceProviderService ? sourceProviderService.getSourceProvider(RepositoryTypes.TfsVersionControl): undefined;

        this._fetchShelvesets();
    }

    public render(): JSX.Element {
        if (this.props.pageContext) {
            const pickerProvider = new PeoplePickerProvider(this.props.pageContext, 
                                    {
                                        options: {
                                            MinResults: 5,
                                            MaxResults: 20
                                        }
                                    });
       
            return <Dialog className="shelveset-dialog"
                           titleProps={{ text: Resources.ShelvesetPickerDialogTitle }}
                           footerButtonProps={[
                                { 
                                    text: Resources.OkButtonText, 
                                    primary: true, 
                                    onClick: this._onClickOk,
                                    disabled: !this.state.selectedShelvesetName
                                },
                                {
                                    text: Resources.CancelButtonText,
                                    onClick: this._onDismiss
                                }
                            ]}
                            onDismiss={this._onDismiss}
                            showCloseButton={true}
                    >
                        <div className="group-add-peoplepicker">
                            <IdentityPickerDropdown pickerProvider={pickerProvider} onChange={this._onPeoplePickerChange} value={this._pickedIdentity} /> 
                            {(this.state.shelvesets && this.state.shelvesets.length>0) && 
                            <Card>
                                <div style={{ display: "flex", height: "300px" }}>
                                    <ScrollableList
                                        itemProvider={new ArrayItemProvider(this.state.shelvesets)}
                                        renderRow={this._renderRow}
                                        onSelect={this._onSelectionChanged}
                                        selection={this._listSelection}
                                        width="100%"
                                    />
                                </div>
                            </Card>} 
                        </div>
                    </Dialog>;
            }

        return <div/>;      
    }

    private _setCurrentUser = () => {
        if (this.props.pageContext)
        {
            const vssPageService = this.props.pageContext.getService<IVssPageService>("IVssPageService");
            const vssPageData = vssPageService.getData();
        
            if (vssPageData) {
                if (vssPageData.user) {
                    this._owner = vssPageData.user;
                }
            }
        }
    }

    private _renderRow = (index: number,  item: TfvcShelvesetRef, details: IListItemDetails<TfvcShelvesetRef>): JSX.Element => {
        const shevesetTitle = item.comment ? format(Resources.ShelvesetNameFormat, item.name, item.comment) : item.name;
        return (
            <ListItem key={ "list-item" + index} index={index} details={details} className="shelveset-row">
                {this._getUserImage(item)}
                <div className="shelveset-info">
                    <span className="shelveset-title text-ellipsis">{shevesetTitle}</span>
                    <span className="shelveset-details">
                        {this._getShelvesetDetails(item)}
                    </span>
                </div>
            </ListItem>
        );
    }

    private _getShelvesetDetails = (shelveset: TfvcShelvesetRef) => {
        let details = "";
        const createdDate = shelveset.createdDate;
        details = format(Resources.ShelvesetCommentFormat, shelveset.owner.displayName, createdDate.toLocaleDateString(), ago(createdDate));
        return details;
    }

    
    private _getUserImage = (shelveset: TfvcShelvesetRef) => {
        // aria-hidden since the name is always in the details.  No reason to read it twice
        return (<span aria-hidden={true} className="shelveset-owner"><BuildPersona
            size={"medium-plus"}
            cssClass={"build-list-persona"}
            displayName={shelveset.owner.displayName}
            imageUrl={getAvatarUrl(shelveset.owner)}
            unloadableImageUrls={this._unloadableImageUrls}
        /></span>);
    }

    private _onSelectionChanged = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<TfvcShelvesetRef>) => {
        const shelveset = this.state.shelvesets[listRow.index];
        if (shelveset)
        {
            this._selectedShelvesetName = shelveset.name + ";" + shelveset.owner.uniqueName;
            this.setState(this._getState());
        }
    }
    
    private _onPeoplePickerChange = (item: IIdentity) => {
        if (item && item.subjectDescriptor) {
            this._owner = this._convertPickerIdentityToIdentityRef(item) || this._owner;
            this._fetchShelvesets();
        }
        this._pickedIdentity.value = item;
    }

    private _convertIdentityRefToPickerIdentity(identityRef?: IdentityRef): IIdentity | undefined {
        if (!identityRef) {
            return undefined;
        }

        return {
            subjectDescriptor: identityRef.descriptor,
            displayName: identityRef.displayName,
            localId: identityRef.id,
            signInAddress: identityRef.uniqueName,
            isHosted: true,
            entityType: ""
        } as IIdentity;
    }

    private _convertPickerIdentityToIdentityRef(identity?: IIdentity): IdentityRef | undefined {
        if (!identity) {
            return undefined;
        }

        return {
            descriptor: identity.subjectDescriptor,
            displayName: identity.displayName,
            id: identity.localId,
            uniqueName: identity.signInAddress
        } as IdentityRef;
    }

    private _fetchShelvesets = () => {
        this._sourceProvider.fetchShelvesets(this._owner.id).then((shelvesets: TfvcShelvesetRef[]) => {
            this._shelvesets = shelvesets.sort((a,b) => (a.createdDate < b.createdDate) ? 1 : ((b.createdDate < a.createdDate) ? -1 : 0)); 
            this.setState(this._getState());
        });
    }

    private _getState = () => {
        return {
            shelvesets: this._shelvesets,
            owner: this._owner,
            selectedShelvesetName: this._selectedShelvesetName,
        }
    }

    private _onClickOk = () => {
        this._actionCreator.closePicker(this.state.selectedShelvesetName);
    }

    private _onDismiss = () => {
        this._actionCreator.closePicker("");
    }
}

interface IDictionaryStringTo<T> {
    [key: string]: T;
}
